import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { SignOut, Smiley, Fire } from "@phosphor-icons/react";
import InvisiqueLogo from "@/components/site/Logo";
import api from "@/lib/api";

/**
 * Portal-only header for the Patient workspace — iter98.35.
 *
 * Mirrors the chrome of DoctorShell / AdminLayout / CoeLayout so all four
 * portals share one visual language: brand wordmark + role pill on the
 * left, identity + logout on the right. Per 2026-06-12 user request,
 * the patient portal was the odd one out (sidebar-only, no top header)
 * which broke the "same design across portals" expectation.
 *
 * Differences vs DoctorShell:
 *  • Role pill reads "PATIENT" with a friendly Smiley glyph instead of
 *    the Stethoscope/IdentificationCard.
 *  • No DraftDrawer (doctor-only concept: in-flight case drafts).
 *  • No NotificationBell yet — there's no /api/patient/notifications
 *    endpoint, so the bell is intentionally omitted rather than render
 *    an always-empty dropdown. Their alerts surface through the
 *    Dashboard hero "Your next step" card, which is the canonical
 *    in-context surface for patients.
 *  • Identity line shows the patient's own name (PortalLayout's avatar
 *    + name remain in the sidebar — same redundancy doctor portal has).
 *  • iter98.36 — Streak chip (🔥 N-day streak) appears next to the
 *    name when the patient has logged 20+ hours of wear on consecutive
 *    days. Surfaced from /api/patient/me/wear-log → summary.streak.
 *    Hides cleanly on day 0 (no streak yet) so the header stays calm.
 *
 * Renders {children} below the header — same contract as DoctorShell so
 * App.js can wrap PortalLayout the same way for both portals.
 */
export default function PatientShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    /* iter98.36 — Streak chip. Reads /patient/me/wear-log on mount and
       surfaces the doctor-spec'd `summary.streak` (consecutive days with
       wear ≥ 20h) as a tiny pill next to the name. Zero-streak hides
       the chip entirely so the header stays calm; first day of a streak
       shows "Day 1" instead of "1-day streak" (warmer copy). Refreshed
       once per mount — daily reload is plenty since streaks only
       update on a wear-log POST anyway. Failures stay silent (the chip
       is delight, not core function). */
    const [streak, setStreak] = useState(0);
    useEffect(() => {
        if (!user || user.role !== "patient") return undefined;
        let cancelled = false;
        api.get("/patient/me/wear-log")
            .then((r) => { if (!cancelled) setStreak(Number(r.data?.summary?.streak) || 0); })
            .catch(() => { /* chip silently absent — non-critical */ });
        return () => { cancelled = true; };
    }, [user]);

    return (
        <div className="min-h-screen bg-secondary/30" data-testid="patient-shell">
            <header className="bg-card border-b border-border sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16">
                    <div className="flex items-center gap-4">
                        <InvisiqueLogo size="sm" />
                        {user && (
                            <div className="hidden sm:flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] tracking-[0.2em] uppercase font-semibold">
                                <Smiley size={10} weight="fill" /> Patient
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        {streak > 0 && (
                            <div
                                className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200/70 text-[11px] font-semibold leading-none"
                                title={`Consecutive days with 20+ hours of wear`}
                                data-testid="patient-streak-chip"
                            >
                                <Fire size={12} weight="fill" className="text-amber-500" />
                                <span data-testid="patient-streak-count">
                                    {streak === 1 ? "Day 1" : `${streak}-day streak`}
                                </span>
                            </div>
                        )}
                        {user && (
                            <div className="hidden sm:block text-right">
                                <div className="text-sm font-medium" data-testid="patient-header-name">
                                    {user.name || user.phone}
                                </div>
                            </div>
                        )}
                        {user && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { logout(); navigate("/patient-login"); }}
                                data-testid="patient-logout-btn"
                            >
                                <SignOut size={14} className="mr-1" /> Logout
                            </Button>
                        )}
                    </div>
                </div>
            </header>
            <main>{children}</main>
        </div>
    );
}
